import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useMutation } from '@apollo/react-hooks';
import { Dropin } from 'braintree-web-drop-in';
import { toast } from 'react-toastify';
import CustomDropIn from '../../../components/Modals/Payment/CustomDropIn';
import {
  CHANGE_MSG,
  CHANGE_PAYMENT_METHOD_MSG,
  SUCESSFULLY_CHANGE_PAYMENT_METHOD_MSG,
  INVALID_BRAINTREE_INSTANCE_MSG,
} from '../../../constants/localization';
import { UPDATE_PAYMENT_SUBSCRIPTIONS } from '../../../constants/gql/payment';
import { tearDownDropInInstance } from '../../../constants/payment';
import { messageFromError } from '../../../constants/ui';
import './ChangePaymentMethodModal.css';
import './ConfirmSubscriptionModal.css';

interface ChangePaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePaymentMethodModal: React.FC<ChangePaymentMethodModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [dropInInstance, setDropInInstance] = useState<Dropin | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updatePaymentMethod] = useMutation(UPDATE_PAYMENT_SUBSCRIPTIONS, {
    refetchQueries: ['me'],
  });

  useEffect(() => {
    if (!isOpen && dropInInstance) {
      try {
        tearDownDropInInstance(dropInInstance, () => setDropInInstance(null));
      } catch (error) {
        console.error('Error tearing down drop-in instance on close:', error);
        setDropInInstance(null);
      }
    }
  }, [isOpen, dropInInstance]);

  useEffect(() => {
    if (isOpen) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }
    return undefined;
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    
    setErrorMessage(null);
    
    if (dropInInstance) {
      try {
        tearDownDropInInstance(dropInInstance, () => {
          setDropInInstance(null);
          onClose();
        });
      } catch (error) {
        console.error('Error tearing down drop-in instance:', error);
        setDropInInstance(null);
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting || !dropInInstance) {
      return;
    }
    setIsSubmitting(true);
    try {
      const paymentMethod = await dropInInstance.requestPaymentMethod();
      await updatePaymentMethod({ variables: { nonce: paymentMethod.nonce } });
      toast(SUCESSFULLY_CHANGE_PAYMENT_METHOD_MSG, { type: 'info' });
      handleClose();
    } catch (error) {
      const message = messageFromError(error as Error);
      setErrorMessage(message || INVALID_BRAINTREE_INSTANCE_MSG);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isConfirmDisabled = useMemo(
    () => !dropInInstance || isSubmitting,
    [dropInInstance, isSubmitting],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="subscription-modal-overlay" onClick={handleClose}>
      <div
        className="subscription-modal-container subscription-payment-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="subscription-modal-close"
          onClick={handleClose}
          aria-label="Close"
          disabled={isSubmitting}
        >
          <X size={20} />
        </button>

        <div className="subscription-modal-content">
          <h2 className="subscription-modal-title">{CHANGE_PAYMENT_METHOD_MSG}</h2>
          <p className="subscription-modal-description">
            Update your saved payment method to keep your subscription active.
          </p>
        </div>

        <div className="subscription-payment-modal-body">
          <CustomDropIn
            setDropInInstance={setDropInInstance}
            dropInInstance={dropInInstance || undefined}
            errorMessage={errorMessage || undefined}
          />
        </div>

        <div className="subscription-modal-actions subscription-payment-modal-actions">
          <button
            type="button"
            className="subscription-modal-secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="subscription-modal-primary"
            onClick={handleSubmit}
            disabled={isConfirmDisabled}
          >
            {isSubmitting ? 'Saving…' : CHANGE_MSG}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePaymentMethodModal;
